//import React from 'react'
import React from 'react'
import {Grid} from '@mui/material'
import AnnouncementsForm from './AnnouncementsForm'
import AnnouncementsTable, {getAnnouncementStatus} from './AnnouncementsTable'
import {makeStyles} from '@mui/styles'
import 'react-sliding-pane/dist/react-sliding-pane.css'
import {useSelector} from 'react-redux'
import DiscardChangesDialog from '../DiscardChangesDialog'
import {useHistory} from 'react-router-dom'
import ConfirmDeleteDialog from '../ConfirmDeleteDialog'
import PageHeader from '../PageHeader'
import Routes from '../../constants/RouteConstants'
import Constant from '../Messages/MessageConstant'
import AnnouncementsConstant from './AnnouncementsConstant'
import EmptyStateIconAndMessage from '../EmptyStateIconAndMessage'
import {RXRButton} from '../RXRButton'
import useFormChanged, {
  ConstructGreaterThanOtherDateValidator,
  DateObjectValidator,
  NonEmptyArrayValidator,
  NonEmptyValidator,
  S3ObjectValidator,
} from '../hooks/useFormChanged'
import {PageContainer, SearchBoxContainer} from '../../assets/styles/layout'
import SidePanel from '../SidePanel'
import {deleteAnnouncement, listAnnouncements, updateAnnouncement, createNewAnnouncement} from '../../lib/queries'
import moment from 'moment'
import useBuildingDataRefresh from '../hooks/useBuildingDataRefresh'
import useListCreateViewPattern from '../hooks/useListCreateViewPattern'
import RXRIcon from '../RXRIcon'
import {spaceMedium} from '../../assets/styles/spacing'
import {H3Header} from '../../assets/styles/typography'
import FileHelper from '../../lib/FileHelper'
import {selectAuthedUserId} from '../../reducers/selectors'
import ClearableInput from '../ClearableInput'
import GenericFilterMenu from '../GenericFilterMenu'

const useStyles = makeStyles(theme => ({
  buttonsContainer: {
    marginTop: spaceMedium,
  },

  filtersContainer: {
    paddingBottom: spaceMedium,
  },

  searchInput: {
    ...SearchBoxContainer,
  },

  longLoadingContainer: {
    textAlign: 'center',
    position: 'absolute',
    width: '100%',
    height: '100%',
    top: 0,
    left: 0,
    backgroundColor: 'rgba(255,255,255,0.8)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 100,

    '& p': {
      ...H3Header,
      margin: spaceMedium,
    },
  },
}))

const EMPTY_FORM_STATE = {
  headline: '',
  activeStart: null,
  activeEnd: null,
  text: '',
  htmlText: '<p></p>', // we start off with an empty p tag because without it the first character entered appears after the caret:  typing "abcd" -> "bcda"
  attachments: [],
  images: [],
  residentIds: [],
  readResidentIds: [],
  audienceLabel: '',
}

const STATUS_FILTER_LABEL_MAP = {
  [AnnouncementsConstant.STATUS_ACTIVE]: AnnouncementsConstant.STATUS_ACTIVE,
  [AnnouncementsConstant.STATUS_INACTIVE]: AnnouncementsConstant.STATUS_INACTIVE,
}

function ViewAnnouncementsPage(props) {
  const isMobile = useSelector(state => state.App.isMobile)
  const classes = useStyles()
  const [announcements, setAnnouncements] = React.useState([])
  const [filterTerm, setFilterTerm] = React.useState('')
  const [filterStatuses, setFilterStatuses] = React.useState(Object.keys(STATUS_FILTER_LABEL_MAP))
  const [deleteDialogAnnouncement, setDeleteDialogAnnouncement] = React.useState(null)
  const [isSaving, setIsSaving] = React.useState(false)
  const history = useHistory()
  const {form, setForm, formChanged, resetInitialForm, invalidItems, validateForm} = useFormChanged(
    EMPTY_FORM_STATE,
    useFormChanged.PropLevelValidation({
      activeStart: DateObjectValidator,
      activeEnd: ConstructGreaterThanOtherDateValidator('activeStart'),
      headline: NonEmptyValidator,
      text: NonEmptyValidator,
      htmlText: NonEmptyValidator,
      images: v => {
        if (!v || v.length === 0) {
          return true
        }
        for (let img of v) {
          if (!S3ObjectValidator(img)) {
            return false
          }
        }
        return true
      },
      residentIds: NonEmptyArrayValidator,
    }),
  )
  const authedStaffId = useSelector(selectAuthedUserId)

  const permissionsObject = useSelector(state => state.GroupPermissions.permissionsObject)
  const {activeBuildingId, isLoadingData: isLoadingAnnouncements} = useBuildingDataRefresh(fetchAnnouncements)

  const {isCreateMode, focusedItemId} = useListCreateViewPattern(
    Routes.ANNOUNCEMENTS_CREATE,
    Routes.ANNOUNCEMENTS_VIEW_SINGLE,
    'announcementId',
    EMPTY_FORM_STATE,
    form,
    resetInitialForm,
    announcements,
    announcement => ({
      id: announcement.id,
      headline: announcement.headline,
      activeStart: new Date(announcement.activeStart),
      activeEnd: new Date(announcement.activeEnd),
      text: announcement.text,
      htmlText: announcement.htmlText,
      attachments: announcement.attachments,
      audienceLabel: announcement.audienceLabel,
      images: announcement.images || [],
      // these are set by the listAnnouncements function in queries.js
      residentIds: announcement.residentIds || [],
      readResidentIds: announcement.readResidentIds || [],
    }),
    isLoadingAnnouncements,
  )

  const resetForm = () => {
    resetInitialForm(EMPTY_FORM_STATE, hideSlidingPanel)
  }

  const hideSlidingPanel = () => {
    history.push(Routes.ANNOUNCEMENTS)
  }

  const handleSave = async () => {
    // make sure the form is valid
    if (!validateForm()) {
      return
    }

    const input = {
      id: form.id,
      text: form.text,
      htmlText: form.htmlText,
      attachments: form.attachments.map(a => FileHelper.formatS3ObjectForInput(a)),
      images: Array.isArray(form.images) ? form.images.map(a => FileHelper.formatS3ObjectForInput(a)) : [],
      activeStart: form.activeStart,
      activeEnd: form.activeEnd,
      headline: form.headline,
      audienceLabel: form.audienceLabel,
      staffId: authedStaffId, // we set it here so that even if it's edited we know who touched it last
    }

    setIsSaving(true)

    try {
      if (form.id) {
        await updateAnnouncement(input)
      } else {
        delete input.id
        input.announcementsBuildingId = activeBuildingId
        await createNewAnnouncement(input)
      }

      await fetchAnnouncements()
      resetForm()
    } catch (er) {
      console.error(er)
      alert('Sorry, we could not process this request. Please re-enter the event information.')
    }
    setIsSaving(false)
  }

  const removeAnnouncement = announcementId => {
    if (!deleteDialogAnnouncement || deleteDialogAnnouncement !== announcementId) {
      setDeleteDialogAnnouncement(announcementId)
      return
    }

    setDeleteDialogAnnouncement(null)
    let itemToRemove = announcements.find(a => a.id === announcementId)
    setAnnouncements(announcements.filter(a => a.id !== announcementId))
    deleteAnnouncement(announcementId).catch(err => {
      // not likely to happen. It's ok if the experience is a little bit janky
      console.error(err)
      window.alert(`Failed to delete ${itemToRemove.headline} event, please try again.`)
      // put the event back into our list
      setAnnouncements([...announcements, itemToRemove])
      history.push(Routes.constructPath(Routes.ANNOUNCEMENTS_VIEW_SINGLE, {eventId: announcementId}))
    })
    resetForm()
  }

  async function fetchAnnouncements() {
    let existingEvents = await listAnnouncements(activeBuildingId)
    let sortedEvents = existingEvents.sort((a, b) => {
      return new Date(a.startTime) - new Date(b.startTime)
    })
    setAnnouncements(sortedEvents)
  }

  const selectedAnnouncement = focusedItemId && announcements.find(a => a.id === focusedItemId)

  const formattedTerm = filterTerm.trim().toLowerCase()
  const filteredAnnouncements = announcements.filter(a => {
    return filterStatuses.includes(getAnnouncementStatus(a)) && (!formattedTerm || a.headline.toLowerCase().includes(formattedTerm))
  })

  return (
    <div style={PageContainer}>
      <PageHeader
        title={'Announcements'}
        rightControlComponent={
          <RXRButton
            type={isMobile ? RXRButton.TYPE_DEFAULT : RXRButton.TYPE_LARGE}
            disabled={!permissionsObject.announcementsWrite}
            onClick={() => history.push(Routes.ANNOUNCEMENTS_CREATE)}
          >
            {AnnouncementsConstant.BTN_ADD}
          </RXRButton>
        }
      >
        <Grid container spacing={3} alignItems="center" justifyContent="space-between" className={classes.filtersContainer}>
          <Grid item xs={12} lg={6} md={6} className={classes.searchInput}>
            <ClearableInput value={filterTerm} onChange={setFilterTerm} placeholder={'Search by title'} />
          </Grid>
          <Grid item>
            <GenericFilterMenu onChange={setFilterStatuses} filterLabelMap={STATUS_FILTER_LABEL_MAP} selectedFilters={filterStatuses}>
              <RXRButton onClick={() => {}} type={RXRButton.TYPE_TEXT}>
                STATUS
              </RXRButton>
            </GenericFilterMenu>
          </Grid>
        </Grid>
      </PageHeader>
      <div style={{height: '100%'}}>
        {announcements.length === 0 ? (
          <EmptyStateIconAndMessage message={Constant.MESSAGE_ANNOUNCEMENT_CONSTANT} icon={RXRIcon.ANNOUNCEMENTS} />
        ) : (
          <AnnouncementsTable announcements={filteredAnnouncements} />
        )}
      </div>
      <DiscardChangesDialog hasChanges={formChanged} />
      <SidePanel
        isOpen={!!focusedItemId || isCreateMode}
        onClose={hideSlidingPanel}
        title={focusedItemId ? 'View announcement' : 'Add new announcement'}
      >
        <AnnouncementsForm invalidItems={invalidItems} {...form} updateForm={setForm} />
        <Grid container spacing={3} alignItems="center" className={classes.buttonsContainer}>
          <Grid item>
            <RXRButton isLoading={isSaving} disabled={!formChanged || !permissionsObject.announcementsWrite} onClick={handleSave}>
              {AnnouncementsConstant.BTN_SAVE}
            </RXRButton>
          </Grid>
          {selectedAnnouncement && (
            <Grid item>
              <RXRButton
                type={RXRButton.TYPE_DESTRUCTIVE}
                disabled={!permissionsObject.announcementsWrite || moment(selectedAnnouncement.activeEnd) < moment()}
                onClick={() => removeAnnouncement(focusedItemId)}
              >
                {AnnouncementsConstant.BTN_DELETE}
              </RXRButton>
            </Grid>
          )}

          <Grid item>
            <RXRButton type={RXRButton.TYPE_TEXT} onClick={hideSlidingPanel}>
              {AnnouncementsConstant.BTN_CANCEL}
            </RXRButton>
          </Grid>
        </Grid>
      </SidePanel>
      <ConfirmDeleteDialog
        prompt={'Are you sure you want to delete this announcement?'}
        isOpen={!!deleteDialogAnnouncement}
        onConfirmDelete={() => removeAnnouncement(deleteDialogAnnouncement)}
        onCancel={() => setDeleteDialogAnnouncement(null)}
      />
    </div>
  )
}

export default ViewAnnouncementsPage
